import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type PatientEncounter = Database["public"]["Tables"]["patient_encounters"]["Row"] & {
  integration_encounter_id?: string | null;
};

interface CreatePatientEncounterData {
  patient_id: string;
  customer_id: string;
  observation_datetime: string;
  patient_age: number;
  patient_sex?: Database["public"]["Enums"]["patient_sexes"];
  specialty: Database["public"]["Enums"]["encounter_speciality_type"];
}

export const usePatientEncounters = (patientId?: string) => {
  return useQuery({
    queryKey: ["patient-encounters", patientId],
    queryFn: async () => {
      let query = supabase.from("patient_encounters").select("*");

      if (patientId) {
        query = query.eq("patient_id", patientId);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;
      return data as PatientEncounter[];
    },
  });
};

export const useCreatePatientEncounter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (encounterData: CreatePatientEncounterData) => {
      const { observation_datetime, ...rest } = encounterData;
      const { data, error } = await supabase
        .from("patient_encounters")
        .insert({
          ...rest,
          admit_datetime: observation_datetime,
          discharge_datetime: null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as PatientEncounter;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-encounters"] });
      toast.success("Patient encounter created successfully");
    },
    onError: (error) => {
      console.error("Error creating patient encounter:", error);
      toast.error("Failed to create patient encounter");
    },
  });
};
